import { Typography } from '@mui/material';
import { BreadcrumbItem, Breadcrumbs } from 'framework/ui/BreadCrumbs';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export interface PageHeaderBreadcrumbLink {
  entity: string;
  link: string;
}

interface PageHeaderBreadcrumbsProps {
  title?: string;
  currentPage?: string;
}

const formatBreadcrumbLabel = (segment: string) =>
  segment
    .split('-')
    .filter(Boolean)
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');

const PageHeaderBreadcrumbs: React.FC<PageHeaderBreadcrumbsProps> = ({ title, currentPage }) => {
  const { pathname } = useLocation();
  const config = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);

    if (segments.length <= 1) {
      return undefined;
    }

    const items: PageHeaderBreadcrumbLink[] = segments.slice(0, -1).map((segment, index) => ({
      entity: formatBreadcrumbLabel(segment),
      link: `/${segments.slice(0, index + 1).join('/')}`,
    }));

    return {
      items,
      currentPage: currentPage || title || formatBreadcrumbLabel(segments[segments.length - 1]),
    };
  }, [currentPage, pathname, title]);

  if (!config) return null;

  const { currentPage: breadcrumbCurrentPage, items } = config;

  return (
    <Breadcrumbs>
      {items.map(({ entity, link }, index) => (
        <BreadcrumbItem key={`${entity}-${link}`} link={link}>
          <Typography color="textSecondary" variant="body2">
            {index > 0 ? entity : `← ${entity}`}
          </Typography>
        </BreadcrumbItem>
      ))}
      {breadcrumbCurrentPage ? (
        <BreadcrumbItem>
          <Typography color="primary" variant="body2">
            {breadcrumbCurrentPage}
          </Typography>
        </BreadcrumbItem>
      ) : null}
    </Breadcrumbs>
  );
};

export default PageHeaderBreadcrumbs;
